// src/pages/ListingDetail.tsx — one advert (Task L): Marketing (the §40 form, pictures, readiness, portal payload) and the
// viewings booked on it. Rent offers live in Lettings → Offers (the legacy offers list went, 2026-10-26). Publish stays disabled until the readiness list is complete (the API refuses too).
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Tabs, PageHeader } from '../components/ui';
import { FormError } from '../components/forms/common';
import { Chip, Section } from '../components/lettings/bits';
import { MarketingForm } from '../components/lettings/MarketingForm';
import { lettingsApi, MARKET_COLOURS, VIEWING_OUTCOMES } from '../lib/lettings';
import { PageLoader } from '../components/BrandLoader';

export default function ListingDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'marketing' | 'activity'>(() => (new URLSearchParams(window.location.search).get('tab') === 'activity' ? 'activity' : 'marketing'));
  const m = useQuery({ queryKey: ['marketing', id], queryFn: () => lettingsApi.marketing(id) });
  const publish = useMutation({
    mutationFn: (on: boolean) => lettingsApi.publish(id, on),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['marketing', id] }); void qc.invalidateQueries({ queryKey: ['/listings'] }); },
  });
  if (!m.data) return m.isError ? <div className="py-20 text-center text-ink-400">Listing not found</div> : <PageLoader />;
  const d = m.data;
  const live = d.status === 'PUBLISHED';
  return (
    <div>
      <PageHeader eyebrow={`${d.property.name} · reference ${d.portals.reference}`} title={d.fields.headline}
        back="/app/marketing/listings"
        actions={<>
          <Chip label={d.marketStatus} colour={MARKET_COLOURS[d.status] ?? '#8fa3b8'} />
          <span className={`whitespace-nowrap text-[12px] font-semibold ${d.readiness.done ? 'text-emerald-700' : 'text-amber-700'}`}>{d.readiness.ok} of {d.readiness.total} ready</span>
        </>}
        menu={[
          ['DRAFT', 'PUBLISHED'].includes(d.status) && (live
            ? { label: 'Take off the market', icon: 'EyeOff', can: 'listings.publish', disabled: publish.isPending, onClick: () => publish.mutate(false) }
            : { label: 'Publish', icon: 'Megaphone', can: 'listings.publish', disabled: !d.readiness.done || publish.isPending, title: d.readiness.done ? 'Put it on the market' : `Still needed: ${d.readiness.missing.join('; ')}`, onClick: () => publish.mutate(true) }),
          live && { label: 'See the advert', icon: 'ExternalLink', href: `/rent/${d.slug}` },
        ]} />
      <FormError error={publish.error} />
      <Tabs className="mb-2.5" value={tab} onChange={setTab} items={[{ key: 'marketing' as const, label: 'Marketing' }, { key: 'activity' as const, label: 'Viewings', count: d.counts.viewings }]} />
      {tab === 'marketing' ? <MarketingForm key={JSON.stringify(d.fields)} m={d} /> : <Activity listingId={id} />}
    </div>
  );
}

function Activity({ listingId }: { listingId: string }) {
  const views = useQuery({ queryKey: ['/viewings', { listingId }], queryFn: async () => (await api.get<Record<string, any>[]>('/viewings', { listingId, order: 'desc', perPage: 50 })).data });
  return (
    <div>
      <Section title={`Viewings · ${views.data?.length ?? 0}`}>
        {!views.data?.length ? <p className="text-[13px] text-ink-500">No viewings yet.</p> : <ul className="divide-y divide-ink-100">{views.data.map((v) => { const oc = VIEWING_OUTCOMES.find((x) => x.key === v.outcome); return <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[13px]"><span>{new Date(v.scheduledAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {v.leadName || '—'}{v.feedback ? <span className="text-ink-400"> · {String(v.feedback).slice(0, 70)}</span> : null}</span>{oc ? <Chip label={oc.label} colour={oc.colour} /> : <Chip label={v.feedbackDue ? 'Feedback due' : v.status.toLowerCase()} colour={v.feedbackDue ? '#d9922e' : '#5b9dff'} />}</li>; })}</ul>}
      </Section>
    </div>
  );
}
